'use client';

import styled from '@emotion/styled';
import { IconButton, Text, Badge, formatPrice } from '@template-project/afrodita-components';
import { ShoppingBag, Trash2 } from 'lucide-react';
import type { CartItemEntity } from '@/domain/entities/cart.entity';
import { useTranslation } from '@/lib/i18n';
import {
  PACK_CART_GROUP_COLOR,
  PACK_CART_GROUP_DECREASE_QUANTITY,
  PACK_CART_GROUP_INCREASE_QUANTITY,
  PACK_CART_GROUP_PACK_APPLIED,
  PACK_CART_GROUP_PACK_FALLBACK,
  PACK_CART_GROUP_REMOVE_PACK,
  PACK_CART_GROUP_SIZE,
} from './i18.keys';
import { useIntl } from '@/lib/intl';
import { usePackName } from '@/lib/hooks/use-pack-name';

const GroupWrapper = styled.div`
  padding: ${({ theme }) => theme.space.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.sm};
  margin-bottom: ${({ theme }) => theme.space.sm};
`;

const PackTitle = styled(Text)`
  flex: 1;
  min-width: 0;
`;

const RemovePackButton = styled(IconButton)`
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-shrink: 0;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.semantic.urgent.color};
    opacity: 1;
  }
`;

const ComponentRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};
  padding: ${({ theme }) => theme.space.xs} 0 ${({ theme }) => theme.space.xs}
    ${({ theme }) => theme.space.sm};
  border-left: 2px solid ${({ theme }) => theme.colors.border.color};
  margin-left: ${({ theme }) => theme.space.xs};
`;

const ComponentImage = styled.img`
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.small};
  flex-shrink: 0;
`;

const ImagePlaceholder = styled.div`
  width: 56px;
  height: 56px;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.radius.small};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.shades[200].color};
`;

const ComponentDetails = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ComponentTitle = styled(Text)``;

const VariantLine = styled(Text)`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: ${({ theme }) => theme.space.md};
  margin-top: ${({ theme }) => theme.space.sm};
`;

const QtyPickerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
`;

const QtyStepperButton = styled(IconButton)`
  width: ${({ theme }) => theme.space['2xl']};
  height: ${({ theme }) => theme.space['2xl']};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const QtyDisplay = styled.span`
  min-width: ${({ theme }) => theme.space['2xl']};
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
`;

const PricingBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const FinalPrice = styled(Text)``;

const ComparePrice = styled(Text)`
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: line-through;
`;

function formatAttributeLabel(
  t: ReturnType<typeof useTranslation>['t'],
  name: string,
  value: string,
): string {
  const lower = name.toLowerCase();
  if (lower === 'color' || lower === 'colour') {
    return t(PACK_CART_GROUP_COLOR, { color: value });
  }
  if (lower === 'size') {
    return t(PACK_CART_GROUP_SIZE, { size: value });
  }
  return `${name}: ${value}`;
}

export interface PackCartGroupProps {
  packId: string;
  items: CartItemEntity[];
  isLoading?: boolean;
  onPackQuantityChange: (packId: string, quantity: number) => void;
  onPackRemove: (packId: string) => void;
}

export function PackCartGroup({
  packId,
  items,
  isLoading = false,
  onPackQuantityChange,
  onPackRemove,
}: PackCartGroupProps) {
  const { t } = useTranslation('sidecart');
  const { countryConfig } = useIntl();
  const { data: packName } = usePackName(packId);

  const priceOptions = {
    currency: countryConfig.currency.code,
    locale: countryConfig.locale,
    symbolOverride: countryConfig.currency.format,
  };

  const packLabel =
    packName ||
    t(PACK_CART_GROUP_PACK_FALLBACK, { id: packId.slice(0, 8) });

  const quantity = items[0]?.quantity ?? 1;

  const lineTotalCents = items.reduce((sum, item) => sum + item.price.display * item.quantity, 0);
  const compareTotalCents = items.reduce((sum, item) => {
    const unit = item.compare_at_price?.display ?? item.price.display;
    return sum + unit * item.quantity;
  }, 0);

  const showCompare = compareTotalCents > lineTotalCents;

  return (
    <GroupWrapper data-loading={isLoading}>
      <HeaderRow>
        <PackTitle variant="bodySmall" weight="bold" as="span">
          {packLabel}
        </PackTitle>
        <RemovePackButton
          type="button"
          variant="destructive"
          size="sm"
          aria-label={t(PACK_CART_GROUP_REMOVE_PACK)}
          onClick={() => onPackRemove(packId)}
          icon={<Trash2 size={18} aria-hidden />}
        />
      </HeaderRow>

      {items.map((item) => (
        <ComponentRow key={item.id}>
          {item.image_url ? (
            <ComponentImage src={item.image_url} alt={item.name} />
          ) : (
            <ImagePlaceholder>
              <ShoppingBag size={24} aria-hidden />
            </ImagePlaceholder>
          )}
          <ComponentDetails>
            <ComponentTitle variant="caption" weight="semibold" as="span">
              {item.name}
            </ComponentTitle>
            {(item.variation_attributes ?? []).map((attr) => (
              <VariantLine key={`${item.id}-${attr.name}`} variant="captionSmall">
                {formatAttributeLabel(t, attr.name, attr.value)}
              </VariantLine>
            ))}
          </ComponentDetails>
        </ComponentRow>
      ))}

      <FooterRow>
        <QtyPickerWrapper>
          <QtyStepperButton
            type="button"
            variant="outline"
            size="sm"
            aria-label={t(PACK_CART_GROUP_DECREASE_QUANTITY)}
            disabled={isLoading || quantity <= 1}
            onClick={() => onPackQuantityChange(packId, quantity - 1)}
            icon={<span>−</span>}
          />
          <QtyDisplay>{quantity}</QtyDisplay>
          <QtyStepperButton
            type="button"
            variant="outline"
            size="sm"
            aria-label={t(PACK_CART_GROUP_INCREASE_QUANTITY)}
            disabled={isLoading}
            onClick={() => onPackQuantityChange(packId, quantity + 1)}
            icon={<span>+</span>}
          />
        </QtyPickerWrapper>

        <PricingBlock>
          <FinalPrice variant="body" weight="bold" as="span">
            {formatPrice(lineTotalCents, priceOptions)}
          </FinalPrice>
          {showCompare && (
            <ComparePrice variant="caption" as="span">
              {formatPrice(compareTotalCents, priceOptions)}
            </ComparePrice>
          )}
          <Badge intent="subtle" variant="success" size="xs" shape="rounded">
            {t(PACK_CART_GROUP_PACK_APPLIED)}
          </Badge>
        </PricingBlock>
      </FooterRow>
    </GroupWrapper>
  );
}
